/**
 * The words for the keys the import speaks in: entities, run statuses, and the
 * reason a legacy table is being left where it is.
 *
 * React-free and lookup-shaped rather than a chain of conditions, for the
 * reason every one of these tables in this CRM is: the fallback is the raw
 * server key, so a value the server learns before the catalogue does draws as
 * itself instead of as an empty cell. A missing entity would otherwise be a
 * blank row in a table whose whole job is saying what will be moved.
 */
import type { StatusTone } from "../../../ui";

/** Entity key -> catalogue key. Keys mirror the server's `IMPORT_ORDER`. */
const ENTITY_KEY: Record<string, string> = {
  clients: "crmImport.entity.clients",
  contacts: "crmImport.entity.contacts",
  leads: "crmImport.entity.leads",
  items: "crmImport.entity.items",
  proposals: "crmImport.entity.proposals",
  estimates: "crmImport.entity.estimates",
  invoices: "crmImport.entity.invoices",
  payments: "crmImport.entity.payments",
  creditnotes: "crmImport.entity.creditnotes",
  expenses: "crmImport.entity.expenses",
  projects: "crmImport.entity.projects",
  milestones: "crmImport.entity.milestones",
  tasks: "crmImport.entity.tasks",
  taskcomments: "crmImport.entity.taskcomments",
  timeentries: "crmImport.entity.timeentries",
  contracts: "crmImport.entity.contracts",
  tickets: "crmImport.entity.tickets",
  cannedreplies: "crmImport.entity.cannedreplies",
  vault: "crmImport.entity.vault",
};

/** Why a non-empty legacy table is being left alone. */
const REASON_KEY: Record<string, string> = {
  register: "crmImport.skipped.reason.register",
  noise: "crmImport.skipped.reason.noise",
  archive: "crmImport.skipped.reason.archive",
  decayed: "crmImport.skipped.reason.decayed",
  manual: "crmImport.skipped.reason.manual",
  unused: "crmImport.skipped.reason.unused",
  ephemeral: "crmImport.skipped.reason.ephemeral",
  lookup: "crmImport.skipped.reason.lookup",
  uncovered: "crmImport.skipped.reason.uncovered",
};

/**
 * The sentence behind each reason.
 *
 * Split from the short label because the skipped list groups by reason: the
 * heading names the decision once and the paragraph argues it once, instead of
 * repeating a three-line justification beside every table it covers.
 */
const WHY_KEY: Record<string, string> = {
  register: "crmImport.skipped.why.register",
  noise: "crmImport.skipped.why.noise",
  archive: "crmImport.skipped.why.archive",
  decayed: "crmImport.skipped.why.decayed",
  manual: "crmImport.skipped.why.manual",
  unused: "crmImport.skipped.why.unused",
  ephemeral: "crmImport.skipped.why.ephemeral",
  lookup: "crmImport.skipped.why.lookup",
  uncovered: "crmImport.skipped.why.uncovered",
};

/** Run lifecycle -> catalogue key. */
const STATUS_KEY: Record<string, string> = {
  running: "crmImport.status.running",
  done: "crmImport.status.done",
  failed: "crmImport.status.failed",
  reverting: "crmImport.status.reverting",
  reverted: "crmImport.status.reverted",
};

/**
 * The pill colour for a run.
 *
 * `reverted` is deliberately muted rather than red: an undo that ran is a
 * finished piece of work, not a failure, and colouring it like one would teach
 * the operator to fear the button this screen exists to offer.
 */
const STATUS_TONE: Record<string, StatusTone> = {
  running: "wt",
  done: "ok",
  failed: "lt",
  reverting: "wt",
  reverted: "mut",
};

type Translate = (key: string) => string;

export function entityLabel(t: Translate, entity: string): string {
  const key = ENTITY_KEY[entity];
  return key ? t(key) : entity;
}

export function reasonLabel(t: Translate, reason: string): string {
  const key = REASON_KEY[reason];
  return key ? t(key) : reason;
}

/**
 * The argument for leaving a table alone, or null when the server sent a reason
 * this catalogue has no sentence for. Null rather than the raw key: a heading
 * can survive being a bare word, a paragraph cannot.
 */
export function reasonWhy(t: Translate, reason: string): string | null {
  const key = WHY_KEY[reason];
  return key ? t(key) : null;
}

export function statusLabel(t: Translate, status: string): string {
  const key = STATUS_KEY[status];
  return key ? t(key) : status;
}

export function statusTone(status: string): StatusTone {
  return STATUS_TONE[status] ?? "info";
}
